import { useState, useEffect, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { llcApi, checkApi, wireApi, achApi, checkLayoutApi } from '../api';
import * as XLSX from 'xlsx';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { FileText, MoreVertical, Trash2, XCircle, CheckCircle, Printer, Eye, RotateCcw, Banknote, Send, Clock, CreditCard, AlertTriangle, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown, Download, RefreshCw, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { CheckPrintView } from '../components/CheckPrintView';
import { WirePrintView } from '../components/WirePrintView';

export const PaymentRegisterPage = () => {
  const [llcs, setLlcs] = useState([]);
  const [selectedLLCId, setSelectedLLCId] = useState(() => {
    return localStorage.getItem('paymentReg_selectedLLC') || 'all';
  });

  const handleLLCFilter = (val) => {
    setSelectedLLCId(val);
    setCurrentPage(1);
    localStorage.setItem('paymentReg_selectedLLC', val);
  };
  const [checks, setChecks] = useState([]);
  const [wires, setWires] = useState([]);
  const [achTransfers, setAchTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkLayout, setCheckLayout] = useState(null);
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [selectedWire, setSelectedWire] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [wireDialogOpen, setWireDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('paymentReg_activeTab') || 'all';
  });
  const [hidePrinted, setHidePrinted] = useState(() => {
    return localStorage.getItem('paymentReg_hidePrinted') === 'true';
  });
  const [hideVoided, setHideVoided] = useState(() => {
    return localStorage.getItem('paymentReg_hideVoided') === 'true';
  });
  const [dateFrom, setDateFrom] = useState(() => {
    return localStorage.getItem('paymentReg_dateFrom') || '';
  });
  const [dateTo, setDateTo] = useState(() => {
    return localStorage.getItem('paymentReg_dateTo') || '';
  });

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    localStorage.setItem('paymentReg_activeTab', tab);
  };
  const [showPrintConfirm, setShowPrintConfirm] = useState(false);
  const [showBatchPrintedConfirm, setShowBatchPrintedConfirm] = useState(false);
  const [batchPrintedIds, setBatchPrintedIds] = useState([]);
  const [sortColumn, setSortColumn] = useState(() => localStorage.getItem('paymentReg_sortCol') || 'date');
  const [sortDirection, setSortDirection] = useState(() => localStorage.getItem('paymentReg_sortDir') || 'desc');
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [voidTargetCheck, setVoidTargetCheck] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [reprintCheck, setReprintCheck] = useState(null);
  const [selectedCheckIds, setSelectedCheckIds] = useState(new Set());
  const [batchVoidOpen, setBatchVoidOpen] = useState(false);
  const [batchVoidReason, setBatchVoidReason] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem('paymentReg_pageSize');
    return saved ? parseInt(saved, 10) : 25;
  });
  const [currentPage, setCurrentPage] = useState(1);

  const checkPrintRef = useRef(null);
  const wirePrintRef = useRef(null);
  const batchPrintRef = useRef(null);
  const reprintRef = useRef(null);

  // Pending non-preprinted checks for batch printing
  const pendingNonPreprinted = checks.filter(c => c.status === 'pending' && !c.is_preprinted);
  
  const handlePrintCheck = useReactToPrint({
    contentRef: checkPrintRef,
    documentTitle: `Check-${selectedCheck?.check_number || 'view'}`,
  });

  const handlePrintWire = useReactToPrint({
    contentRef: wirePrintRef,
    documentTitle: `Wire-${selectedWire?.id?.slice(0, 8) || 'view'}`,
  });

  const handleBatchPrint = useReactToPrint({
    contentRef: batchPrintRef,
    documentTitle: `Checks-Batch-Print`,
    onAfterPrint: () => {
      // Store the IDs and ask the user to confirm they printed
      setBatchPrintedIds(pendingNonPreprinted.map(c => c.id));
      setShowBatchPrintedConfirm(true);
    },
  });

  const handleConfirmBatchPrinted = async () => {
    setShowBatchPrintedConfirm(false);
    try {
      await Promise.all(
        batchPrintedIds.map(id => checkApi.updateStatus(id, 'printed'))
      );
      toast.success(`${batchPrintedIds.length} checks marked as printed`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update check statuses');
    }
    setBatchPrintedIds([]);
  };

  useEffect(() => {
    fetchLLCs();
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedLLCId]);

  const fetchLLCs = async () => {
    try {
      const response = await llcApi.list();
      setLlcs(response.data);
    } catch (error) {
      console.error('Failed to fetch LLCs:', error);
    }
  };

  useEffect(() => {
    checkLayoutApi.get().then(res => setCheckLayout(res.data)).catch(() => {});
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const llcId = selectedLLCId !== 'all' ? selectedLLCId : null;
      const [checksRes, wiresRes, achRes] = await Promise.all([
        checkApi.list(llcId),
        wireApi.list(llcId),
        achApi.list(llcId),
      ]);
      setChecks(checksRes.data);
      // Only include wires marked as "sent" in the payment register
      setWires(wiresRes.data.filter(w => w.status === 'sent'));
      setAchTransfers(achRes.data);
    } catch (error) {
      toast.error('Failed to fetch payment data');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckStatusChange = async (checkId, newStatus, voidReasonText = null) => {
    try {
      await checkApi.updateStatus(checkId, newStatus, voidReasonText);
      toast.success(`Check marked as ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openVoidDialog = (check) => {
    setVoidTargetCheck(check);
    setVoidReason('');
    setVoidDialogOpen(true);
  };

  const confirmVoid = async () => {
    if (!voidTargetCheck) return;
    await handleCheckStatusChange(voidTargetCheck.id, 'voided', voidReason);
    setVoidDialogOpen(false);
    setVoidTargetCheck(null);
    setVoidReason('');
  };

  const handleReprint = useReactToPrint({
    contentRef: reprintRef,
    documentTitle: `Reprint-Check-${reprintCheck?.check_number || 'view'}`,
  });

  const startReprint = (check) => {
    setReprintCheck(check);
    // Wait for next render so the hidden reprint container mounts
    setTimeout(() => handleReprint(), 200);
  };

  const toggleCheckSelection = (checkId) => {
    setSelectedCheckIds(prev => {
      const next = new Set(prev);
      if (next.has(checkId)) next.delete(checkId); else next.add(checkId);
      return next;
    });
  };

  const toggleAllVoidable = () => {
    const voidable = sortedPayments.filter(p => p.type === 'check' && p.status !== 'voided');
    if (selectedCheckIds.size === voidable.length) {
      setSelectedCheckIds(new Set());
    } else {
      setSelectedCheckIds(new Set(voidable.map(p => p.id)));
    }
  };

  const handleBatchVoid = async () => {
    if (selectedCheckIds.size === 0) return;
    try {
      const res = await checkApi.batchVoid([...selectedCheckIds], batchVoidReason);
      toast.success(`${res.data.voided_count} checks voided`);
      setSelectedCheckIds(new Set());
      setBatchVoidOpen(false);
      setBatchVoidReason('');
      fetchData();
    } catch (e) {
      toast.error('Failed to batch void');
    }
  };


  const handleDeleteCheck = async (checkId) => {
    try {
      await checkApi.delete(checkId);
      toast.success('Check deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete check');
    }
  };

  const handleDeleteWire = async (wireId) => {
    try {
      await wireApi.delete(wireId);
      toast.success('Wire deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete wire');
    }
  };

  const handleDeleteACH = async (achId) => {
    try {
      await achApi.delete(achId);
      toast.success('ACH transfer deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete ACH transfer');
    }
  };

  const handleViewCheck = (check) => {
    setSelectedCheck(check);
    setViewDialogOpen(true);
  };

  const handleViewWire = (wire) => {
    setSelectedWire(wire);
    setWireDialogOpen(true);
  };

  const handlePrintCheckFromView = async () => {
    if (selectedCheck?.is_preprinted) {
      setShowPrintConfirm(true);
      return;
    }
    executePrintCheck();
  };

  const executePrintCheck = async () => {
    handlePrintCheck();
    if (selectedCheck && selectedCheck.status === 'pending') {
      try {
        await checkApi.updateStatus(selectedCheck.id, 'printed');
        setSelectedCheck(prev => ({ ...prev, status: 'printed' }));
        fetchData();
      } catch (error) {
        console.error('Failed to update status:', error);
      }
    }
  };

  const handlePrintConfirmed = () => {
    setShowPrintConfirm(false);
    executePrintCheck();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getLLCName = (llcId) => {
    const llc = llcs.find(l => l.id === llcId);
    return llc?.company_name || 'Unknown';
  };

  const getLLC = (llcId) => {
    return llcs.find(l => l.id === llcId);
  };

  const handleExportPayments = () => {
    try {
      if (!sortedPayments.length) {
        toast.error('No payments to export');
        return;
      }
      const rows = sortedPayments.map(p => ({
        'Type': p.type === 'check' ? 'Check' : p.type === 'wire' ? 'Wire' : 'ACH',
        'Ref #': p.type === 'check' ? p.check_number : p.type === 'ach' ? (p.reference || '') : '',
        'Date': p.type === 'check' ? new Date(p.date).toLocaleDateString() : new Date(p.effective_date || p.created_at).toLocaleDateString(),
        'LLC': getLLCName(p.llc_id),
        'Payee/Beneficiary': p.type === 'check' ? p.payee : p.beneficiary_name,
        'Memo/Purpose': p.type === 'check' ? (p.memo || '') : (p.purpose || ''),
        'Amount': p.amount || 0,
        'Status': p.status || '',
        'Void Reason': (p.type === 'check' && p.status === 'voided') ? (p.void_reason || '') : '',
        'Voided Date': (p.type === 'check' && p.voided_at) ? new Date(p.voided_at).toLocaleString() : '',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      // Set column widths
      ws['!cols'] = [
        { wch: 8 }, { wch: 10 }, { wch: 12 }, { wch: 30 },
        { wch: 30 }, { wch: 30 }, { wch: 14 }, { wch: 10 },
        { wch: 35 }, { wch: 20 },
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Payment Register');
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'payment_register.xlsx';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 10000);
      toast.success(`Exported ${rows.length} payments to Excel`);
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export: ' + err.message);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      const newDir = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDir);
      localStorage.setItem('paymentReg_sortDir', newDir);
    } else {
      setSortColumn(column);
      setSortDirection('asc');
      localStorage.setItem('paymentReg_sortCol', column);
      localStorage.setItem('paymentReg_sortDir', 'asc');
    }
  };

  const SortHeader = ({ column, children, className = '' }) => (
    <th
      className={`cursor-pointer select-none hover:bg-slate-100 ${className}`}
      onClick={() => handleSort(column)}
      data-testid={`sort-header-${column}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortColumn === column ? (
          sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
        ) : (
          <ArrowUpDown className="w-3 h-3 opacity-30" />
        )}
      </span>
    </th>
  );

  // Combine all payments
  const allPayments = [
    ...checks.map(c => ({ ...c, type: 'check', sortDate: new Date(c.date) })),
    ...wires.map(w => ({ ...w, type: 'wire', sortDate: new Date(w.created_at) })),
    ...achTransfers.map(a => ({ ...a, type: 'ach', sortDate: new Date(a.effective_date || a.created_at) })),
  ];

  const filteredPayments = (activeTab === 'all' 
    ? allPayments 
    : allPayments.filter(p => p.type === activeTab)
  ).filter(p => !hidePrinted || p.status !== 'printed')
   .filter(p => !hideVoided || p.status !== 'voided')
   .filter(p => {
     if (!searchTerm) return true;
     const q = searchTerm.toLowerCase();
     const ref = p.type === 'check' ? String(p.check_number || '') : (p.id || '').slice(0, 8);
     const payee = p.type === 'check' ? (p.payee || '') : (p.beneficiary_name || p.originator_name || '');
     const memo = p.type === 'check' ? (p.memo || '') : (p.purpose || '');
     const llcName = getLLCName(p.llc_id) || '';
     const amount = String(p.amount || '');
     return ref.toLowerCase().includes(q) || payee.toLowerCase().includes(q) || memo.toLowerCase().includes(q) || llcName.toLowerCase().includes(q) || amount.includes(q);
   })
   .filter(p => {
     if (!dateFrom && !dateTo) return true;
     const d = p.sortDate;
     if (dateFrom && d < new Date(dateFrom)) return false;
     if (dateTo) {
       const end = new Date(dateTo);
       end.setHours(23, 59, 59, 999);
       if (d > end) return false;
     }
     return true;
   });

  // Sort filtered payments
  const sortedPayments = [...filteredPayments].sort((a, b) => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    switch (sortColumn) {
      case 'type':
        return dir * a.type.localeCompare(b.type);
      case 'ref':
        const refA = a.type === 'check' ? (a.check_number || 0) : 0;
        const refB = b.type === 'check' ? (b.check_number || 0) : 0;
        return dir * (refA - refB);
      case 'date':
        return dir * (a.sortDate - b.sortDate);
      case 'llc':
        return dir * (getLLCName(a.llc_id) || '').localeCompare(getLLCName(b.llc_id) || '');
      case 'payee':
        const payeeA = a.type === 'check' ? (a.payee || '') : (a.beneficiary_name || '');
        const payeeB = b.type === 'check' ? (b.payee || '') : (b.beneficiary_name || '');
        return dir * payeeA.localeCompare(payeeB);
      case 'memo':
        const memoA = a.type === 'check' ? (a.memo || '') : (a.purpose || '');
        const memoB = b.type === 'check' ? (b.memo || '') : (b.purpose || '');
        return dir * memoA.localeCompare(memoB);
        return dir * memoA.localeCompare(memoB);
      case 'amount':
        return dir * ((a.amount || 0) - (b.amount || 0));
      case 'status':
        const statusA = a.status || '';
        const statusB = b.status || '';
        return dir * statusA.localeCompare(statusB);
      default:
        return dir * (b.sortDate - a.sortDate);
    }
  });

  // Get only check payments from sorted list for navigation
  const checkPayments = sortedPayments.filter(p => p.type === 'check');

  // Pagination
  const totalItems = sortedPayments.length;
  const totalPages = pageSize === 0 ? 1 : Math.ceil(totalItems / pageSize);
  const safePage = Math.min(currentPage, totalPages || 1);
  const paginatedPayments = pageSize === 0
    ? sortedPayments
    : sortedPayments.slice((safePage - 1) * pageSize, safePage * pageSize);

  const handlePageSizeChange = (val) => {
    const size = val === 'all' ? 0 : parseInt(val, 10);
    setPageSize(size);
    setCurrentPage(1);
    localStorage.setItem('paymentReg_pageSize', size.toString());
  };
  
  const navigateCheck = (direction) => {
    if (!selectedCheck) return;
    const currentIndex = checkPayments.findIndex(p => p.id === selectedCheck.id);
    if (currentIndex === -1) return;
    const newIndex = currentIndex + direction;
    if (newIndex >= 0 && newIndex < checkPayments.length) {
      setSelectedCheck(checkPayments[newIndex]);
    }
  };

  const currentCheckIndex = selectedCheck ? checkPayments.findIndex(p => p.id === selectedCheck.id) : -1;
  const hasPrevCheck = currentCheckIndex > 0;
  const hasNextCheck = currentCheckIndex < checkPayments.length - 1;

  // Calculate totals - separate pending from completed
  const pendingChecks = checks.filter(c => c.status === 'pending');
  const completedChecks = checks.filter(c => c.status === 'printed');
  const voidedChecks = checks.filter(c => c.status === 'voided');
  
  const pendingTotal = pendingChecks.reduce((sum, c) => sum + c.amount, 0);
  const completedCheckTotal = completedChecks.reduce((sum, c) => sum + c.amount, 0);
  const wireTotal = wires.reduce((sum, w) => sum + w.amount, 0);
  const sentACH = achTransfers.filter(a => a.status === 'sent');
  const achTotal = sentACH.reduce((sum, a) => sum + a.amount, 0);
  const completedTotal = completedCheckTotal + wireTotal + achTotal;

  return (
    <div className="space-y-3 fade-in" data-testid="payment-register-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-lg font-semibold text-slate-900 tracking-tight">Payment Register</h1>
          <p className="text-slate-500 mt-1">View all checks, wire transfers, and ACH transfers</p>
        </div>
        <div className="flex items-center gap-3">
          {pendingNonPreprinted.length > 0 && (
            <Button onClick={handleBatchPrint} data-testid="print-all-pending-btn">
              <Printer className="w-4 h-4 mr-2" />
              Print All Pending ({pendingNonPreprinted.length})
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {/* Pending Payments */}
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-amber-700">Pending</p>
                <p className="text-lg font-semibold text-amber-900">{formatCurrency(pendingTotal)}</p>
                <p className="text-xs text-amber-600">{pendingChecks.length} checks awaiting print</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Completed Checks */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Printed Checks</p>
                <p className="text-lg font-semibold text-slate-900">{formatCurrency(completedCheckTotal)}</p>
                <p className="text-xs text-slate-400">{completedChecks.length} printed</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Wire Transfers */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <Banknote className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Wire Transfers</p>
                <p className="text-lg font-semibold text-slate-900">{formatCurrency(wireTotal)}</p>
                <p className="text-xs text-slate-400">{wires.length} sent</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ACH Transfers */}
        <Card className="border-slate-200" data-testid="ach-summary-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-50 rounded-lg">
                <Building2 className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">ACH Transfers</p>
                <p className="text-lg font-semibold text-slate-900">{formatCurrency(achTotal)}</p>
                <p className="text-xs text-slate-400">{sentACH.length} sent, {achTransfers.length - sentACH.length} other</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Completed */}
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CreditCard className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-green-700">Total Completed</p>
                <p className="text-lg font-semibold text-green-900">{formatCurrency(completedTotal)}</p>
                <p className="text-xs text-green-600">{completedChecks.length + wires.length + sentACH.length} payments</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200">
        <CardHeader className="rounded-t-lg panel-header-themed" >
          <CardTitle className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2" style={{ color: '#ffffff' }}>
                <CreditCard className="w-5 h-5" />
                Payment History
              </span>
              <div className="flex items-center gap-4">
                <Tabs value={activeTab} onValueChange={handleTabChange} className="w-auto">
                  <TabsList>
                    <TabsTrigger value="all">All ({allPayments.length})</TabsTrigger>
                    <TabsTrigger value="check">Checks ({checks.length})</TabsTrigger>
                    <TabsTrigger value="wire">Wires ({wires.length})</TabsTrigger>
                    <TabsTrigger value="ach">ACH ({achTransfers.length})</TabsTrigger>
                  </TabsList>
                </Tabs>
                <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="hide-printed-toggle">
                  <input
                    type="checkbox"
                    checked={hidePrinted}
                    onChange={(e) => {
                      setHidePrinted(e.target.checked);
                      setCurrentPage(1);
                      localStorage.setItem('paymentReg_hidePrinted', e.target.checked);
                    }}
                    className="rounded border-slate-400"
                  />
                  <span style={{ color: '#ffffff' }}>Hide Printed</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="hide-voided-toggle">
                  <input
                    type="checkbox"
                    checked={hideVoided}
                    onChange={(e) => {
                      setHideVoided(e.target.checked);
                      setCurrentPage(1);
                      localStorage.setItem('paymentReg_hideVoided', e.target.checked);
                    }}
                    className="rounded border-slate-400"
                  />
                  <span style={{ color: '#ffffff' }}>Hide Voided</span>
                </label>
                <div className="w-48">
                  <Select value={selectedLLCId} onValueChange={handleLLCFilter}>
                    <SelectTrigger data-testid="register-llc-filter" className="h-8 bg-white text-slate-900 text-sm [&>span]:text-slate-900">
                      <SelectValue placeholder="All LLCs" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All LLCs</SelectItem>
                      {llcs.map(llc => (
                        <SelectItem key={llc.id} value={llc.id}>
                          {llc.company_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <button
                  onClick={handleExportPayments}
                  data-testid="export-payments-btn"
                  style={{ backgroundColor: '#ffffff', color: '#1e293b', border: '1px solid #cbd5e1', padding: '4px 12px', borderRadius: '6px', fontSize: '13px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  <Download className="w-4 h-4" />
                  Export Excel
                </button>
                {selectedCheckIds.size > 0 && (
                  <button
                    onClick={() => { setBatchVoidReason(''); setBatchVoidOpen(true); }}
                    data-testid="batch-void-btn"
                    style={{ backgroundColor: '#dc2626', color: '#ffffff', border: 'none', padding: '4px 12px', borderRadius: '6px', fontSize: '13px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    <XCircle className="w-4 h-4" />
                    Void Selected ({selectedCheckIds.size})
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 justify-between">
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  placeholder="Search ref, payee, LLC, memo, amount..."
                  data-testid="payment-search-input"
                  style={{ height: '30px', padding: '2px 8px 2px 30px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', backgroundColor: '#ffffff', color: '#1e293b', width: '300px' }}
                />
                <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                {searchTerm && (
                  <button
                    onClick={() => { setSearchTerm(''); setCurrentPage(1); }}
                    style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '14px', lineHeight: 1 }}
                    data-testid="clear-search-btn"
                  >
                    &times;
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span style={{ color: '#94a3b8', fontSize: '13px' }}>Date Range:</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); localStorage.setItem('paymentReg_dateFrom', e.target.value); }}
                data-testid="date-from-filter"
                style={{ height: '30px', padding: '2px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', backgroundColor: '#ffffff', color: '#1e293b' }}
              />
              <span style={{ color: '#94a3b8', fontSize: '13px' }}>to</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); localStorage.setItem('paymentReg_dateTo', e.target.value); }}
                data-testid="date-to-filter"
                style={{ height: '30px', padding: '2px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', backgroundColor: '#ffffff', color: '#1e293b' }}
              />
              {(dateFrom || dateTo) && (
                <button
                  onClick={() => { setDateFrom(''); setDateTo(''); setCurrentPage(1); localStorage.removeItem('paymentReg_dateFrom'); localStorage.removeItem('paymentReg_dateTo'); }}
                  data-testid="clear-date-filter"
                  style={{ color: '#94a3b8', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline', background: 'none', border: 'none' }}
                >
                  Clear
                </button>
              )}
              </div>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading payments...</div>
          ) : sortedPayments.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p>No payments found</p>
              <p className="text-sm mt-1">
                {activeTab === 'wire' 
                  ? 'Mark wire transfers as "sent" to include them here' 
                  : activeTab === 'ach'
                  ? 'Create ACH transfers to see them here'
                  : 'Create checks, send wires, or initiate ACH transfers to see them here'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[65vh] overflow-y-auto">
              <table className="data-table">
                <thead className="sticky top-0 z-10 bg-slate-50" style={{ boxShadow: '0 1px 0 #e2e8f0' }}>
                  <tr>
                    <th className="w-8 text-center">
                      <input
                        type="checkbox"
                        checked={selectedCheckIds.size > 0 && selectedCheckIds.size === sortedPayments.filter(p => p.type === 'check' && p.status !== 'voided').length}
                        onChange={toggleAllVoidable}
                        className="accent-red-600 w-3.5 h-3.5"
                        title="Select all checks"
                        data-testid="batch-select-all"
                      />
                    </th>
                    <SortHeader column="type">Type</SortHeader>
                    <SortHeader column="ref">Ref #</SortHeader>
                    <SortHeader column="date">Date</SortHeader>
                    {selectedLLCId === 'all' && <SortHeader column="llc">LLC</SortHeader>}
                    <SortHeader column="payee">Payee/Beneficiary</SortHeader>
                    <SortHeader column="memo">Memo/Purpose</SortHeader>
                    <SortHeader column="amount" className="text-right">Amount</SortHeader>
                    <SortHeader column="status">Status</SortHeader>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedPayments.map((payment) => (
                    <tr 
                      key={payment.id} 
                      className={`cursor-pointer hover:bg-slate-100 ${(payment.type === 'check' && payment.status === 'voided') || (payment.type === 'ach' && payment.status === 'cancelled') ? 'opacity-50' : ''}`}
                      onClick={() => payment.type === 'check' ? handleViewCheck(payment) : payment.type === 'wire' ? handleViewWire(payment) : null}
                      data-testid={`payment-row-${payment.id}`}
                    >
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        {payment.type === 'check' && payment.status !== 'voided' ? (
                          <input
                            type="checkbox"
                            checked={selectedCheckIds.has(payment.id)}
                            onChange={() => toggleCheckSelection(payment.id)}
                            className="accent-red-600 w-3.5 h-3.5"
                            data-testid={`batch-check-${payment.id}`}
                          />
                        ) : null}
                      </td>
                      <td>
                        {payment.type === 'check' ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
                            <FileText className="w-3 h-3" />
                            Check
                          </span>
                        ) : payment.type === 'wire' ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">
                            <Send className="w-3 h-3" />
                            Wire
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-600 bg-purple-50 px-2 py-1 rounded">
                            <Building2 className="w-3 h-3" />
                            ACH
                          </span>
                        )}
                      </td>
                      <td className="font-mono font-semibold">
                        {payment.type === 'check' ? payment.check_number : payment.type === 'wire' ? `W-${payment.id.slice(0, 6)}` : (payment.reference || `A-${payment.id.slice(0, 6)}`)}
                      </td>
                      <td>{new Date(payment.type === 'check' ? payment.date : (payment.effective_date || payment.created_at)).toLocaleDateString()}</td>
                      {selectedLLCId === 'all' && <td>{getLLCName(payment.llc_id)}</td>}
                      <td className="max-w-[200px] truncate">
                        {payment.type === 'check' ? payment.payee : payment.beneficiary_name}
                      </td>
                      <td className="max-w-[150px] truncate text-slate-500">
                        {payment.type === 'check' ? (payment.memo || '-') : (payment.purpose || '-')}
                      </td>
                      <td className={`text-right font-mono ${payment.type === 'check' && payment.status === 'voided' ? 'line-through' : 'amount-negative'}`}>
                        {formatCurrency(payment.amount)}
                      </td>
                      <td>
                        {payment.type === 'check' ? (
                          <span className={`status-badge status-${payment.status}`}>
                            {payment.status}
                          </span>
                        ) : payment.type === 'wire' ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">
                            <Send className="w-3 h-3" />
                            sent
                          </span>
                        ) : (
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded ${
                            payment.status === 'sent' ? 'text-green-600 bg-green-50' :
                            payment.status === 'pending' ? 'text-amber-600 bg-amber-50' :
                            payment.status === 'returned' ? 'text-red-600 bg-red-50' :
                            'text-slate-600 bg-slate-50'
                          }`}>
                            {payment.status}
                          </span>
                        )}
                      </td>
                      <td className="text-right" onClick={(e) => e.stopPropagation()}>
                        {payment.type === 'check' ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`check-actions-${payment.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleViewCheck(payment)}>
                                <Eye className="w-4 h-4 mr-2" />
                                View Check
                              </DropdownMenuItem>
                              {payment.status === 'printed' && (
                                <DropdownMenuItem onClick={() => startReprint(payment)} data-testid={`reprint-btn-${payment.id}`}>
                                  <RefreshCw className="w-4 h-4 mr-2" />
                                  Reprint Check
                                </DropdownMenuItem>
                              )}
                              {payment.status === 'pending' && (
                                <DropdownMenuItem onClick={() => handleCheckStatusChange(payment.id, 'printed')}>
                                  <Printer className="w-4 h-4 mr-2" />
                                  Mark as Printed
                                </DropdownMenuItem>
                              )}
                              {payment.status === 'printed' && (
                                <DropdownMenuItem onClick={() => handleCheckStatusChange(payment.id, 'pending')}>
                                  <RotateCcw className="w-4 h-4 mr-2" />
                                  Mark as Unprinted
                                </DropdownMenuItem>
                              )}
                              {payment.status !== 'voided' && (
                                <DropdownMenuItem onClick={() => openVoidDialog(payment)} className="text-red-600" data-testid={`void-btn-${payment.id}`}>
                                  <XCircle className="w-4 h-4 mr-2" />
                                  Void Check
                                </DropdownMenuItem>
                              )}
                              {payment.status === 'voided' && (
                                <DropdownMenuItem onClick={() => handleCheckStatusChange(payment.id, 'pending')}>
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Restore Check
                                </DropdownMenuItem>
                              )}
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-red-600">
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Check?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will permanently delete check #{payment.check_number}. This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDeleteCheck(payment.id)} className="bg-red-600 hover:bg-red-700">
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : payment.type === 'wire' ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`wire-actions-${payment.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleViewWire(payment)}>
                                <Eye className="w-4 h-4 mr-2" />
                                View Wire
                              </DropdownMenuItem>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-red-600">
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Wire?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will permanently delete this wire instruction. This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDeleteWire(payment.id)} className="bg-red-600 hover:bg-red-700">
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`ach-actions-${payment.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {payment.status !== 'sent' && (
                                <DropdownMenuItem onClick={async () => {
                                  try { await achApi.updateStatus(payment.id, 'sent'); toast.success('ACH marked as sent'); fetchData(); } catch(e) { toast.error('Failed to update status'); }
                                }}>
                                  <Send className="w-4 h-4 mr-2" />
                                  Mark as Sent
                                </DropdownMenuItem>
                              )}
                              {payment.status === 'sent' && (
                                <DropdownMenuItem onClick={async () => {
                                  try { await achApi.updateStatus(payment.id, 'returned'); toast.success('ACH marked as returned'); fetchData(); } catch(e) { toast.error('Failed to update status'); }
                                }}>
                                  <RotateCcw className="w-4 h-4 mr-2" />
                                  Mark as Returned
                                </DropdownMenuItem>
                              )}
                              {payment.status !== 'cancelled' && payment.status !== 'sent' && (
                                <DropdownMenuItem onClick={async () => {
                                  try { await achApi.updateStatus(payment.id, 'cancelled'); toast.success('ACH cancelled'); fetchData(); } catch(e) { toast.error('Failed to update status'); }
                                }} className="text-red-600">
                                  <XCircle className="w-4 h-4 mr-2" />
                                  Cancel
                                </DropdownMenuItem>
                              )}
                              {payment.status !== 'sent' && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-red-600">
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Delete ACH Transfer?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        This will permanently delete this ACH transfer to {payment.beneficiary_name}. This action cannot be undone.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => handleDeleteACH(payment.id)} className="bg-red-600 hover:bg-red-700">
                                        Delete
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination Controls */}
              <div className="flex items-center justify-between mt-4 pt-3 border-t text-sm" data-testid="pagination-controls">
                <div className="flex items-center gap-2 text-slate-600">
                  <span>Show</span>
                  <select
                    value={pageSize === 0 ? 'all' : pageSize}
                    onChange={(e) => handlePageSizeChange(e.target.value)}
                    className="border rounded px-2 py-1 text-sm bg-white"
                    data-testid="page-size-select"
                  >
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="all">All</option>
                  </select>
                  <span>of {totalItems} payments</span>
                </div>
                {pageSize !== 0 && totalPages > 1 && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={safePage <= 1}
                      className="px-2 py-1 border rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="page-first"
                    >
                      First
                    </button>
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={safePage <= 1}
                      className="px-2 py-1 border rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="page-prev"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let page;
                      if (totalPages <= 5) {
                        page = i + 1;
                      } else if (safePage <= 3) {
                        page = i + 1;
                      } else if (safePage >= totalPages - 2) {
                        page = totalPages - 4 + i;
                      } else {
                        page = safePage - 2 + i;
                      }
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 py-1 border rounded text-sm ${safePage === page ? 'text-white themed-page-active' : 'hover:bg-slate-100'}`}
                          data-testid={`page-btn-${page}`}
                        >
                          {page}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={safePage >= totalPages}
                      className="px-2 py-1 border rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="page-next"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={safePage >= totalPages}
                      className="px-2 py-1 border rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="page-last"
                    >
                      Last
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Check View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-[900px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" size="sm" 
                  onClick={() => navigateCheck(-1)} 
                  disabled={!hasPrevCheck}
                  data-testid="prev-check-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span>Check #{selectedCheck?.check_number}</span>
                <Button 
                  variant="outline" size="sm" 
                  onClick={() => navigateCheck(1)} 
                  disabled={!hasNextCheck}
                  data-testid="next-check-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <span className="text-sm font-normal text-slate-400 ml-1">
                  {currentCheckIndex + 1} of {checkPayments.length}
                </span>
              </div>
              <div className="flex gap-2 mr-6">
                <Button onClick={handlePrintCheckFromView} data-testid="view-print-btn">
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          {selectedCheck && getLLC(selectedCheck.llc_id) && (
            <div className="mt-4">
              {selectedCheck.is_preprinted && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2" data-testid="preprinted-notice-register">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <div className="text-sm text-amber-800">
                    <span className="font-semibold">Preprinted Check</span>
                    <p className="text-xs text-amber-600 mt-0.5">Load check #{selectedCheck.check_number} into your printer before printing.</p>
                  </div>
                </div>
              )}
              <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
                <div style={{ transform: 'scale(0.95)', transformOrigin: 'top center' }}>
                  <CheckPrintView
                    ref={checkPrintRef}
                    llc={getLLC(selectedCheck.llc_id)}
                    check={selectedCheck}
                    isPreview={false}
                    isPreprinted={selectedCheck.is_preprinted || false}
                    layout={checkLayout}
                  />
                </div>
              </div>
              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500 block">Status</span>
                    <span className={`status-badge status-${selectedCheck.status}`}>
                      {selectedCheck.status}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Created</span>
                    <span className="text-slate-900">{new Date(selectedCheck.created_at).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Amount</span>
                    <span className="text-slate-900 font-mono font-semibold">{formatCurrency(selectedCheck.amount)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">LLC</span>
                    <span className="text-slate-900">{getLLCName(selectedCheck.llc_id)}</span>
                  </div>
                </div>
                {selectedCheck.status === 'voided' && selectedCheck.void_reason && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="void-reason-display">
                    <div className="flex items-start gap-2">
                      <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-xs font-semibold text-red-700 uppercase tracking-wide">Void Reason</span>
                        <p className="text-sm text-red-800 mt-0.5">{selectedCheck.void_reason}</p>
                        {selectedCheck.voided_at && (
                          <p className="text-xs text-red-500 mt-1">Voided {new Date(selectedCheck.voided_at).toLocaleString()}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {selectedCheck.status === 'printed' && selectedCheck.print_snapshot && (
                  <div className="mt-3 flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => startReprint(selectedCheck)} data-testid="view-reprint-btn">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Reprint (Original Snapshot)
                    </Button>
                    <span className="text-xs text-slate-400">Uses the LLC data from when this check was first printed</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Wire View Dialog */}
      <Dialog open={wireDialogOpen} onOpenChange={setWireDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Wire Transfer - {selectedWire?.beneficiary_name}</span>
              <div className="flex gap-2 mr-6">
                <Button onClick={handlePrintWire} data-testid="wire-view-print-btn">
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          {selectedWire && getLLC(selectedWire.llc_id) && (
            <div className="mt-4">
              <div className="border border-slate-200 rounded-lg overflow-auto" style={{ maxHeight: '500px' }}>
                <WirePrintView
                  ref={wirePrintRef}
                  wire={selectedWire}
                  llc={getLLC(selectedWire.llc_id)}
                />
              </div>
              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500 block">Status</span>
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">
                      <Send className="w-3 h-3" />
                      Sent
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Created</span>
                    <span className="text-slate-900">{new Date(selectedWire.created_at).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Amount</span>
                    <span className="text-slate-900 font-mono font-semibold">{formatCurrency(selectedWire.amount)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">LLC</span>
                    <span className="text-slate-900">{getLLCName(selectedWire.llc_id)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Preprinted Check Print Confirmation Dialog */}
      <AlertDialog open={showPrintConfirm} onOpenChange={setShowPrintConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5" />
              Load Preprinted Check Stock
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base">
              Please load check <span className="font-mono font-bold text-slate-900">#{selectedCheck?.check_number}</span> into your printer, then click Print to continue.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handlePrintConfirmed} data-testid="confirm-print-register-btn">
              Print
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Batch Print Confirmation Dialog */}
      <AlertDialog open={showBatchPrintedConfirm} onOpenChange={(open) => {
        if (!open) {
          setShowBatchPrintedConfirm(false);
          setBatchPrintedIds([]);
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark Checks as Printed?</AlertDialogTitle>
            <AlertDialogDescription className="text-base">
              Did you successfully print all {batchPrintedIds.length} checks? This will mark them as printed in the register.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="batch-print-cancel-btn">No, Don't Mark</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmBatchPrinted} data-testid="batch-print-confirm-btn">
              Yes, Mark as Printed
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Hidden batch print container for all pending non-preprinted checks */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        <div ref={batchPrintRef}>
          {pendingNonPreprinted.map((check, index) => {
            const llc = getLLC(check.llc_id);
            if (!llc) return null;
            return (
              <div key={check.id} style={{ pageBreakAfter: index < pendingNonPreprinted.length - 1 ? 'always' : 'auto' }}>
                <CheckPrintView
                  llc={llc}
                  check={check}
                  isPreview={false}
                  isPreprinted={false}
                  layout={checkLayout}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Hidden reprint container — uses print_snapshot LLC data */}
      {reprintCheck && (
        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          <div ref={reprintRef}>
            <CheckPrintView
              llc={reprintCheck.print_snapshot || getLLC(reprintCheck.llc_id)}
              check={reprintCheck}
              isPreview={false}
              isPreprinted={reprintCheck.is_preprinted || false}
              layout={checkLayout}
            />
          </div>
        </div>
      )}


      {/* Batch Void Dialog */}
      <Dialog open={batchVoidOpen} onOpenChange={(open) => { if (!open) { setBatchVoidOpen(false); setBatchVoidReason(''); } }}>
        <DialogContent className="max-w-md" data-testid="batch-void-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" />
              Void {selectedCheckIds.size} Check{selectedCheckIds.size > 1 ? 's' : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Shared void reason <span className="text-slate-400">(applied to all selected)</span>
            </label>
            <textarea
              value={batchVoidReason}
              onChange={(e) => setBatchVoidReason(e.target.value)}
              placeholder="e.g., Account closed, batch reprinted with corrections..."
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-400 resize-none"
              rows={3}
              data-testid="batch-void-reason-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setBatchVoidOpen(false); setBatchVoidReason(''); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleBatchVoid} data-testid="batch-void-confirm-btn">
              Void {selectedCheckIds.size} Check{selectedCheckIds.size > 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void Reason Dialog */}
      <Dialog open={voidDialogOpen} onOpenChange={(open) => { if (!open) { setVoidDialogOpen(false); setVoidTargetCheck(null); setVoidReason(''); } }}>
        <DialogContent className="max-w-md" data-testid="void-reason-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" />
              Void Check #{voidTargetCheck?.check_number}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Reason for voiding <span className="text-slate-400">(optional but recommended)</span>
            </label>
            <textarea
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder="e.g., Wrong amount, duplicate payment, payee changed..."
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-400 resize-none"
              rows={3}
              data-testid="void-reason-input"
              autoFocus
            />
            {voidTargetCheck && (
              <div className="mt-3 text-xs text-slate-500">
                <span className="font-medium">{voidTargetCheck.payee}</span> &middot; {formatCurrency(voidTargetCheck.amount)} &middot; {new Date(voidTargetCheck.date).toLocaleDateString()}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setVoidDialogOpen(false); setVoidTargetCheck(null); setVoidReason(''); }} data-testid="void-cancel-btn">
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmVoid} data-testid="void-confirm-btn">
              Void Check
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
